import type { CheckoutSession, PaymentOption } from "@/types";
import { encodeCurrency } from "@/lib/codec";
import { decodeSessionConfig } from "@/lib/window-session-config";
import type { Tracker } from "./types";

export function getTracker(): Tracker | undefined {
  const w = window as unknown as { tracker?: Tracker };
  return w.tracker;
}

/**
 * Eagerly initializes the global tracker from `window.sessionConfig` when
 * present. Used by handshake so we do not wait on idle-scheduled init.
 */
export function ensureTrackerInitialized(): boolean {
  const tracker = getTracker();
  if (!tracker) {
    return false;
  }
  if (tracker.isInitialized) {
    return true;
  }

  const parsed = decodeSessionConfig();
  if (!parsed || typeof parsed.token !== "string") {
    return false;
  }
  tracker.initialize(parsed.token);
  return tracker.isInitialized;
}

export function trackSessionEvent(
  eventType: string,
  session: CheckoutSession,
  eventData: Record<string, unknown>,
): void {
  const tracker = getTracker();

  if (
    !tracker ||
    !session.merchant?.id ||
    !session.id ||
    !session.order?.id ||
    !session.customerId ||
    !session.order?.countryCode
  ) {
    return;
  }

  const currencyCode = encodeCurrency(session.order.currency) ?? "SAR";

  tracker.track({
    eventType,
    merchantId: session.merchant.id,
    checkoutId: session.id,
    orderId: session.order.id,
    customerId: session.customerId,
    countryCode: session.order.countryCode,
    currencyCode,
    eventData,
  });
}

export function buildPpwsPayload(
  paymentOption: PaymentOption,
  session: CheckoutSession,
  paymentType:
    | "PAY_BY_INSTALMENTS"
    | "PAY_NOW"
    | "PAY_NEXT_MONTH" = "PAY_BY_INSTALMENTS",
  isEligiblePaymentOption?: boolean,
): Record<string, unknown> {
  const currency = paymentOption.downPaymentAmount.currency;
  const orderTotal = session.orderTotalAmount.amount;
  const instalments = paymentOption.tenure;

  const repayments =
    instalments === 0
      ? []
      : paymentOption.schedulePreview
          .filter((schedule) => !schedule.dueNow)
          .map((schedule) => ({
            type: "repayment",
            amount: {
              amount: schedule.amount.amount,
              currency: schedule.amount.currency,
            },
            due_date: schedule.dueDate,
            cart_amount: {
              amount: 0.0,
              currency: schedule.amount.currency,
            },
            original_due_date: schedule.dueDate,
            customer_service_fee: {
              fixed_service_fee: { amount: 0.0, currency: schedule.amount.currency },
              variable_service_fee: { amount: 0.0, currency: schedule.amount.currency },
              vat_fixed_service_fee: { amount: 0.0, currency: schedule.amount.currency },
              vat_variable_service_fee: { amount: 0.0, currency: schedule.amount.currency },
            },
            customer_service_fee_rate: null,
          }));

  const downpaymentSchedule = paymentOption.schedulePreview.find(
    (schedule) => schedule.dueNow,
  );

  const downpaymentAmount = downpaymentSchedule
    ? downpaymentSchedule.amount.amount
    : paymentOption.downPaymentAmount.amount;

  const totalCustomerFee = paymentOption.totalCustomerFeeAmount.amount;
  const totalPayments = instalments === 0 ? 1 : instalments + 1;
  const feePerPayment =
    totalPayments > 0 ? totalCustomerFee / totalPayments : 0;
  const vatRate = 0.15;

  const downpaymentVariableFee = feePerPayment;
  const downpaymentVatFee = downpaymentVariableFee * vatRate;

  const repaymentVariableFee = feePerPayment;
  const repaymentVatFee = repaymentVariableFee * vatRate;

  const repaymentsWithFees =
    instalments === 0
      ? []
      : repayments.map((repayment) => ({
          ...repayment,
          customer_service_fee: {
            fixed_service_fee: { amount: 0.0, currency: repayment.amount.currency },
            variable_service_fee: { amount: repaymentVariableFee, currency: repayment.amount.currency },
            vat_fixed_service_fee: { amount: 0.0, currency: repayment.amount.currency },
            vat_variable_service_fee: { amount: repaymentVatFee, currency: repayment.amount.currency },
          },
        }));

  const downpayment = {
    type: "down_payment",
    amount: downpaymentAmount,
    currency,
    customer_service_fee: {
      fixed_service_fee: { amount: 0.0, currency },
      variable_service_fee: { amount: downpaymentVariableFee, currency },
      vat_fixed_service_fee: { amount: 0.0, currency },
      vat_variable_service_fee: { amount: downpaymentVatFee, currency },
    },
  };

  const walletDownpayment =
    session.paymentDetails?.walletDownPaymentAmount?.amount ?? 0.0;
  const rewardDownpayment = 0.0;

  const optionRecord = paymentOption as unknown as Record<string, unknown>;

  return {
    metadata: {
      discount: 0.0,
      instalments,
      order_total: orderTotal,
      payment_plan: {
        repayments: repaymentsWithFees,
        downpayment,
        is_waived_off: paymentOption.waivedOffFee !== null,
        reward_downpayment: { amount: rewardDownpayment, currency },
        wallet_downpayment: { amount: walletDownpayment, currency },
        customer_service_fee: {
          amount: paymentOption.totalCustomerFeeAmount.amount,
          currency: paymentOption.totalCustomerFeeAmount.currency,
        },
        is_amount_below_csf_threshold: false,
        waive_off_customer_service_fee: {
          amount: paymentOption.waivedOffFee?.amount ?? 0.0,
          currency: paymentOption.waivedOffFee?.currency ?? currency,
        },
      },
      payment_type: paymentType,
      cashback_info: optionRecord.cashback ?? null,
      credit_amount: paymentOption.downPaymentAmount.amount,
      loan_offer_id: paymentOption.offerId,
      apple_pay_status: {
        is_iframe: false,
        apple_pay_preference: false,
        is_enabled_apple_pay: false,
        is_apple_pay_available: false,
      },
      cashback_was_shown: false,
      is_unequal_repayment: paymentOption.equalDownPaymentStrategy === false,
      is_eligible_payment_option:
        isEligiblePaymentOption !== undefined ? isEligiblePaymentOption : true,
    },
    order_id: session.orderId,
    time_stamp: Math.floor(Date.now() / 1000),
  };
}
